const FOREGROUND_CHECK_INTERVAL_MS = 60_000;

/** Discover new builds when an installed app resumes, without touching its data caches. */
export function watchServiceWorkerUpdates(
  registration: Pick<ServiceWorkerRegistration, 'update'>,
): () => void {
  let disposed = false;
  let checking = false;
  let lastAttempt: number | undefined;

  const check = async (reconnected = false): Promise<void> => {
    if (disposed || checking || document.visibilityState !== 'visible' || !navigator.onLine) return;
    const now = Date.now();
    if (
      !reconnected &&
      lastAttempt !== undefined &&
      now - lastAttempt < FOREGROUND_CHECK_INTERVAL_MS
    ) {
      return;
    }
    checking = true;
    lastAttempt = now;
    try {
      // Unlike updateServiceWorker(), update() actually fetches/checks for a new worker.
      await registration.update();
    } catch {
      // Offline transitions, expired Access sessions, and server failures must not break local work.
    } finally {
      checking = false;
    }
  };

  const onForeground = (): void => {
    void check();
  };
  const onOnline = (): void => {
    void check(true);
  };

  document.addEventListener('visibilitychange', onForeground);
  window.addEventListener('focus', onForeground);
  window.addEventListener('pageshow', onForeground);
  window.addEventListener('online', onOnline);
  onForeground();

  return () => {
    disposed = true;
    document.removeEventListener('visibilitychange', onForeground);
    window.removeEventListener('focus', onForeground);
    window.removeEventListener('pageshow', onForeground);
    window.removeEventListener('online', onOnline);
  };
}
